import { actions } from "@/actions";
import { DeleteCollectionsModalStore } from "@/components/modal/delete-collections/delete-collections-modal-store";
import { DeleteSharedNotesModalStore } from "@/components/modal/delete-shared-notes/delete-shared-notes-modal-store";
import { MakeCollectionPrivateModalStore } from "@/components/modal/make-collection-private/make-collection-private-modal-store";
import { RemoveNotesFromSharedCollectionModalStore } from "@/components/modal/remove-notes-from-shared-collection/remove-notes-from-shared-collection-modal-store";
import { ShareSheetModalStore } from "@/components/modal/share-sheet/share-sheet-modal-store";
import { ShareSheetEntityKind } from "@/components/modal/share-sheet/types";
import { MDSIconKind, MDSMenuContentItem, MDSMenuContentItemKind, MDSMenuContentList } from "@/mds";
import { ItemListRowData, ItemListRowType } from "@/components/item-list/types";
import { Maybe } from "@/domains/common/types";
import { Uuid } from "@/domains/global/identifiers";
import localDb from "@/domains/local-db";
import { EventContext } from "@/domains/metrics/context";
import { getObjectAfterDeletion } from "@/domains/react/useObjectAfterDeletion";
import { NotesListFilter } from "@/modules/filters/notes/NotesListFilter";
import { LensKind, SortByKind } from "@/modules/lenses/types";
import { logger } from "@/modules/logger";
import { objectModule } from "@/modules/object";
import { groupLens } from "@/modules/timeline";
import { uuidModule } from "@/modules/uuid";
import { resolveNoteContentDocumentSyncModelUuid } from "@/modules/uuid/sync-models/resolveNoteContentDocumentSyncModelUuid";
import { CollectionObservable } from "@/store/collections/CollectionObservable";
import { INoteObservable, NotesIndexTuple, NotesSortByKind } from "@/store/note";
import { NoteCreateGateDeniedError } from "@/store/note/AppStoreNoteStore";
import { PaywallContext } from "@/domains/metrics/tracked-event";
import { getItemListRowForNote } from "@/components/item-list/helpers";
import { LeaveCollectionMessageModalStore } from "@/store/pages/CollectionsViewPageStore/LeaveCollectionMessageModalStore";
import { ListStateObservable } from "@/store/pages/ListStateObservable";
import { MountedStore } from "@/store/pages/MountedStore";
import { TemplateIndexTuple } from "@/store/templates/types";
import { AppSubStore, AppSubStoreArgs } from "@/store/types";
import { getLocalTimeZone } from "@internationalized/date";
import Dexie, { liveQuery, Subscription } from "dexie";
import {
  action,
  computed,
  makeObservable,
  observable,
  onBecomeObserved,
  reaction,
  runInAction,
} from "mobx";
import { itemsModule } from "@/modules/items";

export class CollectionsViewPageStore extends AppSubStore {
  tabId: string;
  collectionId: string;
  isSidePanel: boolean;

  supportsAddToCollectionListAction = true;
  supportsRemoveFromCollectionListAction = true;
  supportsMoveNoteToTrashListAction = true;
  supportsChatListAction = true;
  supportsAutoOrganizeListAction = false;
  isLoading: boolean;

  listState: ListStateObservable;
  deleteCollectionsModal: DeleteCollectionsModalStore;
  deleteSharedNotesModal: DeleteSharedNotesModalStore;
  removeNotesFromSharedCollectionModal: RemoveNotesFromSharedCollectionModalStore;
  shareSheetModal: ShareSheetModalStore;
  makeCollectionPrivateModal: MakeCollectionPrivateModalStore;
  leaveCollectionMessageModal: LeaveCollectionMessageModalStore;

  mountState = new MountedStore();

  /** This is a proxy with isDeleted=true after the note is gone. */
  collectionObservable: Maybe<CollectionObservable>;
  couldAccessCollectionOnMount: Maybe<boolean>;

  shareSheetEntityId: Uuid | undefined;
  shareSheetEntityKind: ShareSheetEntityKind | undefined;

  isSubscriptionInitialized = false;
  noteLiveQuerySubscription: Maybe<Subscription>;
  subscribedNoteRows: NotesIndexTuple[] = [];
  templateLiveQuerySubscription: Maybe<Subscription>;
  subscribedTemplateRows: TemplateIndexTuple[] = [];

  eventContext = EventContext.CollectionMultiselectActions;

  filters: NotesListFilter;

  // allows to skip unnecessary reactions
  private initialized: boolean = false;
  // ref to current scroll position of virtualized list
  private currentScrollTop = 0;

  private readonly supportedSortOptions: SortByKind[] = [
    SortByKind.LastModified,
    SortByKind.LastCreated,
    SortByKind.LastAdded,
  ];

  constructor(
    injectedDeps: AppSubStoreArgs & { isSidePanel?: boolean; tabId: string; collectionId: string }
  ) {
    super(injectedDeps);
    this.tabId = injectedDeps.tabId;
    this.collectionId = injectedDeps.collectionId;

    this.listState = new ListStateObservable({ ...injectedDeps, listStateProvider: this });
    this.deleteCollectionsModal = new DeleteCollectionsModalStore(injectedDeps, {});
    this.deleteSharedNotesModal = new DeleteSharedNotesModalStore(injectedDeps, {});
    this.removeNotesFromSharedCollectionModal = new RemoveNotesFromSharedCollectionModalStore(
      injectedDeps,
      {}
    );
    this.shareSheetModal = ShareSheetModalStore.forAppStore({ appStore: this.store });
    this.makeCollectionPrivateModal = new MakeCollectionPrivateModalStore(injectedDeps);
    this.filters = new NotesListFilter(this.store, this.supportedSortOptions);
    this.isLoading = true;
    this.isSidePanel = injectedDeps.isSidePanel ?? false;

    makeObservable<
      this,
      | "generateItemDropdown"
      | "initializeNoteLiveQuery"
      | "initializeTemplateLiveQuery"
      | "supportedSortOptions"
      | "getNotesSort"
      | "getCollectionItemsFilter"
      | "initialized"
      | "currentScrollTop"
    >(this, {
      supportsChatListAction: true,
      tabId: observable,
      collectionId: observable,
      setCollectionId: action,

      initialized: false,
      isSidePanel: observable,
      getNotesSort: true,
      getCollectionItemsFilter: true,
      useEffects: true,

      // Subscriptions
      subscribedNoteRows: observable,
      subscribedTemplateRows: observable,
      isSubscriptionInitialized: observable,
      noteLiveQuerySubscription: observable,
      templateLiveQuerySubscription: observable,
      initializeNoteLiveQuery: action,
      initializeTemplateLiveQuery: action,

      supportsAddToCollectionListAction: false,
      supportsRemoveFromCollectionListAction: false,
      supportsMoveNoteToTrashListAction: false,
      supportsAutoOrganizeListAction: false,
      listState: false,
      deleteCollectionsModal: false,
      deleteSharedNotesModal: false,
      removeNotesFromSharedCollectionModal: false,
      shareSheetModal: false,
      makeCollectionPrivateModal: false,
      leaveCollectionMessageModal: false,
      mountState: false,
      eventContext: false,
      filters: false,
      supportedSortOptions: false,
      // Mounting + Access Changes
      initialize: false,
      amIMounted: computed,
      showNotFound: true,
      collectionObservable: observable,
      setCollectionObservable: true,
      canAccessCollection: true,
      couldAccessCollectionOnMount: observable,
      setCouldAccessCollectionOnMount: true,
      shareSheetEntityId: observable,
      shareSheetEntityKind: observable,
      collection: computed,
      collectionMultiSelectActionsContext: computed,
      generateItemDropdown: false,
      orderedItemIds: computed,

      headerDropdownButtonContentList: computed,
      handleCreateNewNote: action,
      handleChatWithCollection: action,
      handleLeaveCollection: action,
      hasNoItems: computed,
      isLoading: observable,
      groupedLensItems: computed,
      itemRows: computed,

      // virtualized list index
      currentScrollTop: observable,
      scrollTop: computed,
      setScrollTop: action,
    });

    // We need this to be already observable to react to changes its observable props.
    this.leaveCollectionMessageModal = new LeaveCollectionMessageModalStore(this);

    reaction(
      () => this.filters.params,
      (params, previousParams) => {
        if (!this.collection?.id) return;
        this.store.memDb.settings.setCollectionsViewPageParams(this.collection.id, params);

        if (
          params.lens !== previousParams.lens ||
          params.sortBy !== previousParams.sortBy ||
          params.filter !== previousParams.filter
        ) {
          this.setScrollTop(0);
        }
      }
    );

    reaction(
      () => ({ collection: this.collection, amIMounted: this.amIMounted }),
      ({ collection: maybeCollection, amIMounted }, { collection: previousCollection }) => {
        const collection = getObjectAfterDeletion({
          current: maybeCollection,
          previous: previousCollection,
          active: amIMounted,
        });

        this.setCollectionObservable(collection);
        this.filters.setExcludeCollection([collection]);
      }
    );

    reaction(
      () => this.collection?.canAccess,
      (canAccess, previousCanAccess) => {
        const couldAccessCollectionOnMount = (() => {
          // Getting access later is fine.
          if (canAccess) return true;
          // Prefer the previous value if it's already set.
          if (typeof previousCanAccess === "boolean") return previousCanAccess;
          // Either undefined (_unknown_) or false.
          return canAccess;
        })();
        this.setCouldAccessCollectionOnMount(couldAccessCollectionOnMount);
      }
    );

    onBecomeObserved(this, "subscribedNoteRows", () => this.initialize());
    onBecomeObserved(this, "subscribedTemplateRows", () => this.initialize());

    reaction(
      () => this.isSubscriptionInitialized,
      initialized => {
        if (initialized) setTimeout(() => runInAction(() => (this.isLoading = false)), 100);
      }
    );
  }

  setCollectionId(collectionId: string) {
    this.collectionId = collectionId;
  }

  setCollectionObservable(collectionObservable?: CollectionObservable) {
    this.collectionObservable = collectionObservable;
  }

  setCouldAccessCollectionOnMount(couldAccessCollectionOnMount?: boolean) {
    this.couldAccessCollectionOnMount = couldAccessCollectionOnMount;
  }

  get scrollTop() {
    return this.currentScrollTop;
  }

  get amIMounted() {
    return this.mountState.isMounted;
  }

  get showNotFound() {
    if (!this.couldAccessCollectionOnMount) return true;

    if (this.leaveCollectionMessageModal.isModalOpen) return false;

    return !this.canAccessCollection;
  }

  get canAccessCollection(): boolean {
    // Return true when it's unknown.
    if (!this.mountState.isMounted || !this.collectionId) return true;

    const doesNotExist = this.store.collections.doesNotExist(this.collectionId);
    if (doesNotExist) return false;

    const canAccess = this.store.collections.get(this.collectionId)?.canAccess;

    return canAccess !== false;
  }

  get collection(): Maybe<CollectionObservable> {
    if (!this.mountState.isMounted) return;

    if (!this.collectionId) return;
    return this.store.collections.get(this.collectionId);
  }

  get hasNoItems() {
    return !this.isLoading && this.subscribedNoteRows.length === 0;
  }

  get collectionMultiSelectActionsContext() {
    return this.collection;
  }

  private generateItemDropdown({
    noteObservable,
  }: {
    noteObservable: INoteObservable;
  }): MDSMenuContentList {
    return [
      {
        id: `remove-collection-${noteObservable.id}`,
        kind: MDSMenuContentItemKind.Item,
        label: "Remove note from collection",
        onClick: () => {
          if (!this.collection) return;
          if (this.collection.isShared) {
            this.removeNotesFromSharedCollectionModal.open({
              notes: [noteObservable],
              collection: this.collection,
            });
          } else {
            actions.removeNoteFromCollection({
              note: noteObservable,
              collection: this.collection,
              store: this.store,
            });
          }
        },
        icon: MDSIconKind.Exit,
      },
      {
        id: "divider-1",
        kind: MDSMenuContentItemKind.Divider,
      },
      {
        id: `share-${noteObservable.id}`,
        kind: MDSMenuContentItemKind.Item,
        icon: MDSIconKind.Share,
        label: "Share",
        onClick: () =>
          this.shareSheetModal.open({
            id: noteObservable.id,
            entityKind: ShareSheetEntityKind.Note,
            eventContext: EventContext.CollectionRowActions,
          }),
      },
      {
        id: `copy-link-${noteObservable.id}`,
        kind: MDSMenuContentItemKind.Item,
        label: "Copy link",
        onClick: () => actions.copyNoteLinkToClipboard({ noteId: noteObservable.id }),
        icon: MDSIconKind.Copy,
      },
      {
        id: `favorite-${noteObservable.id}`,
        kind: MDSMenuContentItemKind.Item,
        icon: noteObservable.isFavorited ? MDSIconKind.ThumbtackSolid : MDSIconKind.Thumbtack,
        label: noteObservable.isFavorited ? "Unpin" : "Pin",
        onClick: async () => await noteObservable.toggleFavorite(),
      },
      {
        id: "divider-2",
        kind: MDSMenuContentItemKind.Divider,
      },
      {
        id: `move-to-trash-${noteObservable.id}`,
        kind: MDSMenuContentItemKind.Item,
        label: "Trash",
        onClick: async () => {
          if (noteObservable.isShared) {
            this.deleteSharedNotesModal.open(noteObservable);
          } else {
            await noteObservable.moveToTrash();
          }
        },
        icon: MDSIconKind.Trash,
      },
    ];
  }

  get groupedLensItems() {
    return groupLens(
      this.subscribedNoteRows.map(tuple => ({
        dateTime: new Date(tuple.length === 4 ? tuple[2] : tuple[1]),
        item: tuple,
      }))
    );
  }

  get itemRows(): ItemListRowData[] {
    const output: ItemListRowData[] = [];
    if (!this.collection) {
      return output;
    }

    for (const group of this.groupedLensItems) {
      output.push({
        type: ItemListRowType.SectionHeader,
        key: group.title,
        payload: { title: group.title },
      });

      for (const item of group.items) {
        const tuple = item.item;
        const itemId = tuple.length === 4 ? tuple[3] : tuple[2];
        const itemAppearance = itemsModule.resolveAppearance({ itemId, appStore: this.store });

        output.push({
          type: ItemListRowType.AsyncNote,
          key: itemId,
          payload: {
            noteId: itemId,
            itemRow: note =>
              getItemListRowForNote({
                dateTime: item.dateTime,
                dropdown: this.generateItemDropdown({ noteObservable: note }),
                inMainPanel: true,
                listState: this.listState,
                noteObservable: note,
                store: this.store,
                tabId: this.tabId,
                appearance: itemAppearance,
              }),
          },
        });
      }
    }

    if (output.length) {
      output.push({
        type: ItemListRowType.Padding,
        key: "padding",
        payload: { height: 50 },
      });
    }

    return output;
  }

  get orderedItemIds() {
    return this.itemRows.map(row => row.key);
  }

  // HEADER
  get headerDropdownButtonContentList(): MDSMenuContentList {
    const optionalActions: MDSMenuContentItem[] = this.collection?.isOwnedByMe
      ? [
          { id: "collection-divider", kind: MDSMenuContentItemKind.Divider },
          {
            id: "delete-collection",
            kind: MDSMenuContentItemKind.Item,
            label: "Delete",
            onClick: () =>
              this.deleteCollectionsModal.open({
                collectionIds: this.collection ? [this.collection.id] : [],
              }),
            icon: MDSIconKind.Trash,
          },
        ]
      : [
          {
            id: "leave-collection",
            kind: MDSMenuContentItemKind.Item,
            label: "Leave collection",
            onClick: async () => await this.handleLeaveCollection(),
            icon: MDSIconKind.Trash,
          },
        ];

    if (this.collection?.isShared && this.collection.isOwnedByMe) {
      optionalActions.splice(1, 0, {
        id: "make-collection-private",
        kind: MDSMenuContentItemKind.Item,
        label: "Make this collection private",
        onClick: () => this.makeCollectionPrivateModal.open({ collectionId: this.collection?.id }),
        icon: MDSIconKind.Lock,
      });
    }

    return [
      {
        id: "copy-collection-link",
        kind: MDSMenuContentItemKind.Item,
        label: "Copy link",
        icon: MDSIconKind.Copy,
        onClick: () => {
          if (!this.collection) return;
          actions.copyCollectionLinkToClipboard({ collectionId: this.collection.id });
        },
      },
      ...optionalActions,
    ];
  }

  public async handleCreateNewNote({ autoStartVoice }: { autoStartVoice?: boolean } = {}) {
    if (!this.collection) {
      logger.debug({
        message: "handleCreateNewNote: collection not found",
      });

      return;
    }

    const collectionId = this.collection.id;
    const noteId = uuidModule.generate();
    const noteContentId = resolveNoteContentDocumentSyncModelUuid({ noteId });

    try {
      // Note: `createNote` enforces the usage gate and throws `NoteCreateGateDeniedError` when blocked.
      // Callers must catch that error to avoid triggering follow-up work or logging as fatal.
      await this.store.notes.createNote({
        noteId,
        eventContext: EventContext.CollectionActions,
        paywallContext: PaywallContext.CollectionList,
      });
    } catch (error) {
      if (error instanceof NoteCreateGateDeniedError) {
        return;
      }
      logger.error({
        message: "handleCreateNewNote: failed to create note",
        info: { error: objectModule.safeErrorAsJson(error as Error), noteId, collectionId },
      });
      return;
    }
    const [note, _noteContent] = await Promise.all([
      this.store.notes.getAsync(noteId),
      this.store.noteContentDocuments.getAsync(noteContentId),
    ]);

    if (!note) {
      logger.debug({
        message: "handleCreateNewNote: noteObservable not found upon creation",
        info: { noteId },
      });
      return;
    }

    // Delay adding to the collection to avoid a flash on the page
    // This is not problematic since the note is already guaranteed to exist when we navigate to the editor
    setTimeout(() => {
      note.collectionList?.addCollection({
        collectionId,
        eventContext: EventContext.CollectionActions,
      });
    }, 50);

    this.store.navigation.goToNote({ noteId, autoFocus: true, autoStartVoice });
  }

  public async handleChatWithCollection({ overrideContext = false } = {}) {
    const collection = this.collectionObservable;
    if (!collection) return;

    await actions.startChatWithEntities({
      collectionIds: [collection.id],
      store: this.store,
      overrideContext,
    });
  }

  public async handleLeaveCollection() {
    const mySpaceAccountId = this.store.spaceAccounts.myPersonalSpaceAccountId;
    if (!this.collection || !mySpaceAccountId) return;
    await this.collection.revokeAccessViaSpaceAccount({
      targetSpaceAccountId: mySpaceAccountId,
    });
    this.store.navigation.goToCollections();
  }

  setScrollTop = (value: number) => {
    this.currentScrollTop = value;
  };

  useEffects() {
    this.mountState.useEffects();
  }

  private initializeNoteLiveQuery() {
    reaction(
      () => ({
        collectionId: this.collectionId,
        params: this.filters.params,
      }),
      ({ collectionId, params }) => {
        if (!collectionId) {
          this.subscribedNoteRows = [];
          this.isSubscriptionInitialized = false;
          this.isLoading = false;
          return;
        }

        const { sortBy, lens, filter } = params;
        const collectionItemAddedAt: Record<string, string> = {};
        const dateRange = this.filters.dateSelectedRange;
        const sortByKind = sortBy as SortByKind;
        this.isLoading = true;

        this.noteLiveQuerySubscription?.unsubscribe();
        this.noteLiveQuerySubscription = liveQuery(async () => {
          // Collect Collection Items first, applying AddedBy/At filter and AddedAt Sort if necessary
          const collectionItemTuples = await Dexie.waitFor(
            this.store.collectionItems.search({
              collectionId,
              sortBy: SortByKind.LastAdded,
              filter: this.getCollectionItemsFilter(sortByKind),
            })
          );

          // Collect Note Ids and AddedAt
          const ids: string[] = [];
          collectionItemTuples.forEach(tuple => {
            collectionItemAddedAt[tuple[1]] = tuple[2];
            ids.push(tuple[1]);
          });

          if (ids.length === 0) {
            return [];
          }

          // TODO: for now its only notes but in future CollectionItems store should be able to handle this
          const filterOption = NotesListFilter.dateFilterMap[sortBy];

          return this.store.notes.search({
            lens,
            sortBy: this.getNotesSort(sortByKind),
            limit: 10000, // TODO: refactor to use CollectionItems store and better indexing
            filter: {
              ...filter,
              ids,
              [filterOption]: {
                from: dateRange?.start?.toDate(getLocalTimeZone()).toString(),
                to: dateRange?.end?.add({ days: 1 }).toDate(getLocalTimeZone()).toString(),
                olderThan: filter?.date?.olderThan,
              },
              collections: filter?.collections ? [filter.collections] : undefined,
            },
          });
        }).subscribe({
          next: tuples => {
            runInAction(() => {
              this.subscribedNoteRows = tuples;
              this.isLoading = false;

              // substitute date field with added_at which not part of the note model
              // TODO: we can do better than this by indexing more on Collection item
              if (sortByKind === SortByKind.LastAdded) {
                this.subscribedNoteRows = tuples
                  .map(tuple => [tuple[0], collectionItemAddedAt[tuple[2]] || "", tuple[2]])
                  .sort(
                    (a, b) => new Date(b[1]).getTime() - new Date(a[1]).getTime()
                  ) as NotesIndexTuple[];
              }

              this.isSubscriptionInitialized = true;
            });
          },
        });
      },
      { fireImmediately: true }
    );
  }

  private initializeTemplateLiveQuery() {
    reaction(
      () => this.collectionId,
      collectionId => {
        this.templateLiveQuerySubscription?.unsubscribe();
        this.templateLiveQuerySubscription = liveQuery(async () => {
          if (!collectionId) {
            this.subscribedTemplateRows = [];
            return;
          }
          return this.store.templates.search({
            lens: LensKind.All,
            filter: {
              collections: {
                ids: [collectionId],
                mode: "any",
              },
            },
          });
        }).subscribe({
          next: templateIndexTuples => {
            runInAction(() => {
              this.subscribedTemplateRows = templateIndexTuples || [];
            });
          },
        });
      },
      { fireImmediately: true }
    );
  }

  private getCollectionItemsFilter(sortBy: SortByKind) {
    const filter = this.filters.params.filter;
    const dateRange = this.filters.dateSelectedRange;

    if (sortBy !== SortByKind.LastAdded) {
      return {
        addedBy: filter?.addedBy,
      };
    }

    return {
      addedBy: filter?.addedBy,
      addedAt: {
        from: dateRange?.start?.toDate(getLocalTimeZone()).toString(),
        to: dateRange?.end?.add({ days: 1 }).toDate(getLocalTimeZone()).toString(),
        olderThan: filter?.date?.olderThan,
      },
    };
  }

  private getNotesSort(sortBy: SortByKind): NotesSortByKind {
    // notes search doesn't support sortByKind.LastAdded
    if (sortBy === SortByKind.LastAdded || sortBy === SortByKind.LastUsed) {
      return SortByKind.LastModified;
    }

    return sortBy;
  }

  async initialize() {
    const collectionId = this.collectionId;
    if (!collectionId) {
      return;
    }

    if (this.initialized) return;

    this.initialized = true;

    const [localDBParams, memDbParams] = await Promise.all([
      localDb.settings.getCollectionsViewPageParams(collectionId),
      this.store.memDb.settings.getCollectionsViewPageParams(collectionId),
    ]);

    if (localDBParams?.sortBy && this.store.memDb) {
      await this.store.memDb.settings.setCollectionsViewPageParams(collectionId, localDBParams);

      await localDb.settings.removeCollectionsViewPageParams(collectionId);
    }

    this.filters.clearAllFilters();
    this.filters.setParams({
      lens: localDBParams?.lens ?? memDbParams?.lens ?? NotesListFilter.DEFAULT_LENS,
      sortBy: localDBParams?.sortBy ?? memDbParams?.sortBy ?? NotesListFilter.DEFAULT_SORT_BY,
      filter: localDBParams?.filter ?? memDbParams?.filter,
    });

    this.initializeNoteLiveQuery();
    this.initializeTemplateLiveQuery();
  }
}
